/**
 * Intake state.
 *
 * Owns the form data, the draft token, and the upload queue. The important
 * property of this provider is that **the server copy is authoritative from
 * step one** — local state is a working copy that is pushed up as the customer
 * moves through the form, not a buffer that is submitted at the end. Closing
 * the tab therefore costs the customer nothing but their place in the queue.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import * as intakeApi from '../services/intakeApi';
import { compressImage } from '../lib/images';
import { tokenFor, latestDraftFor, rememberDraft, forgetDraft } from '../lib/draftStorage';

const IntakeContext = createContext(null);

const AUTOSAVE_DELAY_MS = 900;

const EMPTY_FORM = {
  // Customer
  firstName: '',
  lastName: '',
  phone: '',
  email: '',

  // Vehicle
  registration: '',
  make: '',
  otherMake: '',
  model: '',
  otherModel: '',
  year: '',
  colour: '',

  // Damage and insurance
  damageDescription: '',
  isInsuranceWork: false,
  insuranceCompany: '',
  otherInsurer: '',
  claimMade: '',
  excess: '',

  // Extras
  needsLoanVehicle: false,
  selectedServices: {},

  // What the customer said they would do about photos
  photosPromised: ''
};

// The form no longer collects an address — the shop asks for it when it needs
// one. The API still accepts those fields for other intake paths.
const CUSTOMER_KEYS = ['firstName', 'lastName', 'phone', 'email'];
const VEHICLE_KEYS = ['registration', 'make', 'otherMake', 'model', 'otherModel', 'year', 'colour'];
const QUOTE_REQUEST_KEYS = ['damageDescription', 'isInsuranceWork', 'insuranceCompany', 'otherInsurer', 'claimMade', 'excess', 'needsLoanVehicle', 'selectedServices', 'photosPromised'];

/** Take only the listed keys — the API returns record metadata we don't want in form state. */
function pick(source, keys) {
  const out = {};
  if (!source) return out;
  for (const key of keys) {
    if (source[key] !== undefined) out[key] = source[key];
  }
  return out;
}

/**
 * Fill blanks in the working copy from the server's, never the other way round.
 *
 * Used when the server hands back a request the customer already had open: they
 * have just typed their name and may already be typing a registration, and
 * having the form rewrite itself underneath them would be worse than missing a
 * field they can re-enter.
 */
function mergeBlanks(current, incoming) {
  const out = { ...current };

  for (const [key, value] of Object.entries(incoming)) {
    const existing = out[key];
    const existingIsBlank = existing === '' || existing === undefined || existing === null
      || (typeof existing === 'object' && Object.keys(existing).length === 0)
      || (typeof existing === 'boolean' && existing === false);

    const incomingHasValue = value !== '' && value !== undefined && value !== null
      && !(typeof value === 'object' && Object.keys(value).length === 0);

    if (existingIsBlank && incomingHasValue) out[key] = value;
  }

  return out;
}

/** Split a flat form patch into the three records the API writes to. */
function toApiPatch(patch) {
  const customer = {};
  const vehicle = {};
  const quoteRequest = {};

  for (const [key, value] of Object.entries(patch)) {
    if (CUSTOMER_KEYS.includes(key)) customer[key] = value;
    else if (VEHICLE_KEYS.includes(key)) vehicle[key] = value;
    else if (QUOTE_REQUEST_KEYS.includes(key)) quoteRequest[key] = value;
  }

  const out = {};
  if (Object.keys(customer).length) out.customer = customer;
  if (Object.keys(vehicle).length) out.vehicle = vehicle;
  if (Object.keys(quoteRequest).length) {
    if (quoteRequest.selectedServices && typeof quoteRequest.selectedServices !== 'string') {
      quoteRequest.selectedServices = JSON.stringify(quoteRequest.selectedServices);
    }
    out.quoteRequest = quoteRequest;
  }
  return out;
}

export function IntakeProvider({ teamId, resumeToken, urlQuoteRequestId, onQuoteRequestId, children }) {
  const [organisation, setOrganisation] = useState(null);
  const [loadState, setLoadState] = useState({ loading: true, error: null });

  const [form, setForm] = useState(EMPTY_FORM);
  const [token, setToken] = useState(null);
  const [quoteRequestId, setQuoteRequestId] = useState(null);
  const [returningCustomer, setReturningCustomer] = useState(false);
  const [submittedAt, setSubmittedAt] = useState(null);

  // What the submit call reported about the acknowledgement email. The
  // confirmation page reads it: the customer's way back into their request is
  // that email, so when it did not go out the page has to offer the link
  // itself. Null until this browser has actually submitted.
  const [delivery, setDelivery] = useState(null);

  const [photos, setPhotos] = useState([]);      // uploaded: { id, category, label, order, preview? }
  const [uploads, setUploads] = useState([]);    // in flight: { key, category, label, preview, status, error }
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const tokenRef = useRef(null);
  const pendingPatch = useRef({});
  const saveTimer = useRef(null);
  const previewsAttempted = useRef(new Set());
  // In-flight creation, so a double-tap on step 1 can't create two requests.
  const draftRequest = useRef(null);
  // The contact details creation was attempted with, kept for retries.
  const contactRef = useRef(null);

  // Only ever set, never cleared: background creation writes the ref directly
  // and a render that happens before `token` state catches up must not wipe it.
  if (token) tokenRef.current = token;

  /**
   * Held in a ref, not a dependency.
   *
   * `useNavigate` returns a new function on every location change, so the
   * callback built from it changes on every step. With it in the load effect's
   * dependency array the whole initial load re-ran on each navigation and
   * `setPhotos(draft.images)` replaced the in-session photos with server copies
   * that carry no local preview — which is why photos went blank the moment you
   * left the review page and came back.
   */
  const publishId = useRef(onQuoteRequestId);
  publishId.current = onQuoteRequestId;

  // The initial load belongs to one request; a second run would fight with it.
  const loadedFor = useRef(null);
  const quoteRequestIdRef = useRef(null);
  quoteRequestIdRef.current = quoteRequestId;

  // ---- Load: validate the shop, then resume anything already in progress ----
  useEffect(() => {
    let cancelled = false;

    async function load() {
      // Already working on the request the URL names. This is the common case
      // right after creation, when `?q=…` is added to the address bar — without
      // it the effect would re-run and replace the photos taken in this session
      // with server copies that have no local preview.
      if (urlQuoteRequestId && quoteRequestIdRef.current === urlQuoteRequestId) return;

      // Guard against a re-run: the effect keys on the shop and the link, so
      // anything else changing must not restart it.
      const key = `${teamId}|${resumeToken || ''}|${urlQuoteRequestId || ''}`;
      if (loadedFor.current === key) return;
      loadedFor.current = key;

      setLoadState({ loading: true, error: null });

      const result = await intakeApi.getOrganisation(teamId);
      if (cancelled) return;

      if (!result.valid) {
        setLoadState({ loading: false, error: result.error });
        return;
      }
      setOrganisation(result.organisation);

      // A request id in the URL wins over "whatever this browser did last" —
      // the customer followed a specific link.
      const stored = urlQuoteRequestId
        ? { quoteRequestId: urlQuoteRequestId, token: tokenFor(urlQuoteRequestId) }
        : latestDraftFor(teamId);
      const candidate = resumeToken || stored?.token;

      if (candidate) {
        try {
          const draft = await intakeApi.getDraft(candidate);
          if (cancelled) return;

          setToken(candidate);
          setQuoteRequestId(draft.quoteRequest.id);
          setSubmittedAt(draft.quoteRequest.submittedAt);
          setPhotos(draft.images || []);
          setForm((current) => ({
            ...current,
            ...pick(draft.customer, CUSTOMER_KEYS),
            ...pick(draft.vehicle, VEHICLE_KEYS),
            ...pick(draft.quoteRequest, QUOTE_REQUEST_KEYS),
            // Numeric columns come back as numbers; the inputs are text.
            year: draft.vehicle?.year ? String(draft.vehicle.year) : '',
            excess: draft.quoteRequest?.excess ? String(draft.quoteRequest.excess) : '',
            selectedServices: safeParse(draft.quoteRequest?.selectedServices, {})
          }));
          rememberDraft(teamId, draft.quoteRequest.id, candidate);
          publishId.current?.(draft.quoteRequest.id);
        } catch (err) {
          // An expired or revoked link is not an error the customer can act on —
          // drop it and let them start again rather than blocking the form.
          console.warn('Could not resume draft:', err.message);
          forgetDraft(teamId, stored?.quoteRequestId);
        }
      }

      if (!cancelled) setLoadState({ loading: false, error: null });
    }

    load();
    return () => { cancelled = true; };
  }, [teamId, resumeToken, urlQuoteRequestId]);

  // ---- Saving ----

  const flush = useCallback(async () => {
    // Before the request exists there is nowhere to send anything — the queue
    // must survive until startDraft flushes it, or anything typed while
    // creation was in flight is silently dropped.
    if (!tokenRef.current || !Object.keys(pendingPatch.current).length) return;

    const patch = pendingPatch.current;
    pendingPatch.current = {};

    const apiPatch = toApiPatch(patch);
    if (!Object.keys(apiPatch).length) return;

    setSaving(true);
    try {
      await intakeApi.updateDraft(tokenRef.current, apiPatch);
      setSaveError(null);
    } catch (err) {
      // Put the fields back so the next flush retries them.
      pendingPatch.current = { ...patch, ...pendingPatch.current };
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  }, []);

  /** Update local state and queue the change for the server. */
  const update = useCallback((patch) => {
    setForm((current) => ({ ...current, ...patch }));
    pendingPatch.current = { ...pendingPatch.current, ...patch };

    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flush, AUTOSAVE_DELAY_MS);
  }, [flush]);

  /**
   * Create the quote request, in the background.
   *
   * Deliberately NOT awaited by the caller: making the customer watch a spinner
   * on step 1 is the most expensive place in the form to add friction, and the
   * creation has nothing they need to see. They move to step 2 immediately and
   * the record appears a moment later.
   *
   * Everything downstream already copes with the token arriving late — edits
   * queue in `pendingPatch` and flush the moment it lands — so the only thing
   * this has to guarantee is that it happens exactly once, and that a failure
   * is retried rather than lost.
   */
  const startDraft = useCallback((contact) => {
    if (tokenRef.current) return Promise.resolve();
    if (draftRequest.current) return draftRequest.current;

    // Remembered so a failed attempt can be retried from any later step.
    contactRef.current = {
      firstName: contact.firstName || '',
      lastName: contact.lastName || '',
      phone: contact.phone || '',
      email: contact.email || ''
    };

    setSaving(true);
    draftRequest.current = intakeApi.createDraft({ teamId, customer: contactRef.current })
      .then(async (result) => {
        tokenRef.current = result.draftToken;
        setToken(result.draftToken);
        setQuoteRequestId(result.quoteRequestId);
        setReturningCustomer(result.returningCustomer);
        setSaveError(null);
        rememberDraft(teamId, result.quoteRequestId, result.draftToken);
        publishId.current?.(result.quoteRequestId);

        // The server may have handed back a request this customer already had
        // open. Fill only what they haven't typed in this session, so resuming
        // never overwrites something they are part-way through.
        if (result.resumed) {
          try {
            const draft = await intakeApi.getDraft(result.draftToken);
            setPhotos(draft.images || []);
            setSubmittedAt(draft.quoteRequest.submittedAt);
            setForm((current) => mergeBlanks(current, {
              ...pick(draft.customer, CUSTOMER_KEYS),
              ...pick(draft.vehicle, VEHICLE_KEYS),
              ...pick(draft.quoteRequest, QUOTE_REQUEST_KEYS),
              year: draft.vehicle?.year ? String(draft.vehicle.year) : '',
              excess: draft.quoteRequest?.excess ? String(draft.quoteRequest.excess) : '',
              selectedServices: safeParse(draft.quoteRequest?.selectedServices, {})
            }));
          } catch (err) {
            console.warn('[intake] could not load the resumed request:', err.message);
          }
        }

        // Whatever they typed while this was in flight still has to go up.
        await flush();
        return result;
      })
      .catch((err) => {
        // Clearing the handle allows a retry; the queued edits are untouched.
        draftRequest.current = null;
        setSaveError(err.message);
        throw err;
      })
      .finally(() => setSaving(false));

    return draftRequest.current;
  }, [teamId, flush]);

  /**
   * Push everything queued immediately — used when leaving a step.
   *
   * Doubles as the retry point for a failed background creation: every later
   * step calls this on continue, so a customer who got past step 1 while the
   * network was down still ends up with a saved request without being told
   * anything went wrong.
   */
  const save = useCallback(async () => {
    clearTimeout(saveTimer.current);

    if (!tokenRef.current && contactRef.current) {
      await startDraft(contactRef.current).catch(() => null);
      return; // startDraft flushes on success.
    }

    await flush();
  }, [flush, startDraft]);

  /**
   * Wait for the background creation to finish, retrying it if it failed.
   *
   * Anything that needs the token itself — uploading a photo, submitting —
   * goes through this rather than reading `tokenRef` directly. A customer on a
   * slow connection can reach the photo step before creation lands, and a photo
   * posted with a null token fails in a way they can do nothing about.
   *
   * @returns {Promise<string|null>} the token, or null if creation is failing
   */
  const awaitDraft = useCallback(async () => {
    if (tokenRef.current) return tokenRef.current;

    if (draftRequest.current) {
      await draftRequest.current.catch(() => null);
    } else if (contactRef.current) {
      await startDraft(contactRef.current).catch(() => null);
    }

    return tokenRef.current;
  }, [startDraft]);

  // ---- Photos ----

  /**
   * Compress and upload one photo. Uploads are independent: a failure costs
   * that photo only, and the customer can retry it from the review step.
   */
  const uploadPhoto = useCallback(async (source, { category = 'extra', label = '' } = {}) => {
    const key = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    let prepared;
    try {
      prepared = await compressImage(source);
    } catch (err) {
      setUploads((current) => [...current, { key, category, label, status: 'failed', error: err.message }]);
      return { ok: false, error: err.message };
    }

    setUploads((current) => [
      ...current,
      { key, category, label, preview: prepared.dataUrl, status: 'uploading', error: null }
    ]);

    // Compressing took a moment anyway; if creation is still in flight this
    // usually returns instantly.
    const draftToken = await awaitDraft();
    if (!draftToken) {
      const message = 'Still saving your details — try that photo again in a moment';
      setUploads((current) => current.map((upload) => (
        upload.key === key ? { ...upload, status: 'failed', error: message } : upload
      )));
      return { ok: false, error: message };
    }

    try {
      const { image } = await intakeApi.addImage(draftToken, {
        data: prepared.dataUrl,
        filename: `${category}-${key}.jpg`,
        category,
        label,
        mimeType: 'image/jpeg'
      });

      setPhotos((current) => [...current, { ...image, preview: prepared.dataUrl }]);
      setUploads((current) => current.filter((upload) => upload.key !== key));
      return { ok: true, image };
    } catch (err) {
      setUploads((current) => current.map((upload) => (
        upload.key === key ? { ...upload, status: 'failed', error: err.message } : upload
      )));
      return { ok: false, error: err.message };
    }
  }, [awaitDraft]);

  const retryUpload = useCallback(async (key) => {
    const upload = uploads.find((item) => item.key === key);
    if (!upload?.preview) return;

    setUploads((current) => current.map((item) => (
      item.key === key ? { ...item, status: 'uploading', error: null } : item
    )));

    try {
      const { image } = await intakeApi.addImage(tokenRef.current, {
        data: upload.preview,
        filename: `${upload.category}-${key}.jpg`,
        category: upload.category,
        label: upload.label,
        mimeType: 'image/jpeg'
      });
      setPhotos((current) => [...current, { ...image, preview: upload.preview }]);
      setUploads((current) => current.filter((item) => item.key !== key));
    } catch (err) {
      setUploads((current) => current.map((item) => (
        item.key === key ? { ...item, status: 'failed', error: err.message } : item
      )));
    }
  }, [uploads]);

  const discardUpload = useCallback((key) => {
    setUploads((current) => current.filter((item) => item.key !== key));
  }, []);

  /**
   * Swap one photo for another, keeping its category and label so a replaced
   * "Front Right" is still filed as the front right angle.
   */
  const replacePhoto = useCallback(async (imageId, source) => {
    const existing = photos.find((photo) => photo.id === imageId);
    if (!existing) return { ok: false, error: 'That photo is no longer here' };

    const result = await uploadPhoto(source, {
      category: existing.category,
      label: existing.label
    });
    if (!result.ok) return result;

    // Only remove the old one once the new one is safely up.
    try {
      await intakeApi.deleteImage(tokenRef.current, imageId);
      setPhotos((current) => current.filter((photo) => photo.id !== imageId));
    } catch (err) {
      console.warn('[intake] replaced photo, but the old one could not be removed:', err.message);
    }

    return result;
  }, [photos, uploadPhoto]);

  const removePhoto = useCallback(async (imageId) => {
    const previous = photos;
    setPhotos((current) => current.filter((photo) => photo.id !== imageId));
    try {
      await intakeApi.deleteImage(tokenRef.current, imageId);
    } catch (err) {
      setPhotos(previous);
      throw err;
    }
  }, [photos]);

  /**
   * Photos uploaded on another device have no local preview — the bucket is
   * team-only, so thumbnails come back through the function.
   */
  const loadMissingPreviews = useCallback(async () => {
    // Track what has been tried: a preview that fails would otherwise be
    // requested again on every render that follows, since it stays "missing".
    const missing = photos.filter((photo) => !photo.preview && !previewsAttempted.current.has(photo.id));
    if (!missing.length) return;

    missing.forEach((photo) => previewsAttempted.current.add(photo.id));

    const results = await Promise.all(missing.map(async (photo) => {
      try {
        const { dataUrl } = await intakeApi.getImagePreview(tokenRef.current, photo.id);
        return { id: photo.id, preview: dataUrl };
      } catch {
        return null;
      }
    }));

    setPhotos((current) => current.map((photo) => {
      const found = results.find((result) => result?.id === photo.id);
      return found ? { ...photo, preview: found.preview } : photo;
    }));
  }, [photos]);

  // ---- Submit ----

  const resumeUrl = useMemo(() => (
    token ? `${window.location.origin}/r/${token}` : ''
  ), [token]);

  const submit = useCallback(async ({ photosPromised } = {}) => {
    // If background creation never succeeded, this is the last chance to make
    // it work — and the one moment the customer is willing to wait.
    const draftToken = await awaitDraft();
    if (!draftToken) {
      throw new Error('We could not save your request. Please check your connection and try again.');
    }

    await save();
    const result = await intakeApi.submitDraft(draftToken, { photosPromised, resumeUrl });
    setSubmittedAt(new Date().toISOString());
    setDelivery({ emailSent: result?.emailSent === true, emailReason: result?.emailReason || null });
    return result;
  }, [save, resumeUrl, awaitDraft]);

  const emailResumeLink = useCallback(async (email) => {
    return intakeApi.sendResumeLink(tokenRef.current, { email, resumeUrl });
  }, [resumeUrl]);

  // Only unsent work is worth warning about — everything else is already saved.
  useEffect(() => {
    const inFlight = uploads.length > 0;
    if (!inFlight) return undefined;

    const handler = (event) => {
      event.preventDefault();
      event.returnValue = 'Some photos are still uploading. Leave anyway?';
      return event.returnValue;
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [uploads.length]);

  useEffect(() => () => clearTimeout(saveTimer.current), []);

  const value = useMemo(() => ({
    organisation,
    loading: loadState.loading,
    error: loadState.error,

    form,
    update,
    save,
    saving,
    saveError,

    token,
    quoteRequestId,
    returningCustomer,
    submittedAt,
    delivery,
    started: Boolean(token),
    startDraft,
    resumeUrl,

    photos,
    uploads,
    uploadPhoto,
    replacePhoto,
    retryUpload,
    discardUpload,
    removePhoto,
    loadMissingPreviews,

    submit,
    emailResumeLink,
    reset: () => forgetDraft(teamId, quoteRequestId)
  }), [
    organisation, loadState, form, update, save, saving, saveError, token, quoteRequestId,
    returningCustomer, submittedAt, delivery, startDraft, resumeUrl, photos, uploads, uploadPhoto, replacePhoto,
    retryUpload, discardUpload, removePhoto, loadMissingPreviews, submit, emailResumeLink, teamId
  ]);

  return <IntakeContext.Provider value={value}>{children}</IntakeContext.Provider>;
}

export function useIntake() {
  const context = useContext(IntakeContext);
  if (!context) throw new Error('useIntake must be used inside an IntakeProvider');
  return context;
}

function safeParse(value, fallback) {
  if (!value || typeof value !== 'string') return value || fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}
